import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SafeIcon from '../components/common/SafeIcon';
import { useAuth } from '../context/AuthContext';
import { getRoleLabel } from '../constants/roles';
import { mockLeads } from '../data/mockProfile';

export default function MyProfilePage() {
  const navigate = useNavigate();
  const { profile, updateOwnProfile, signOut } = useAuth();
  const [isLandingActive, setIsLandingActive] = useState(profile?.landing_active ?? false);
  const [phone, setPhone] = useState(profile?.phone ?? '');
  const [saving, setSaving] = useState(false);
  const [savingPhone, setSavingPhone] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  useEffect(() => {
    if (profile) {
      setIsLandingActive(profile.landing_active ?? false);
      setPhone(profile.phone ?? '');
    }
  }, [profile?.landing_active, profile?.phone, profile]);

  if (!profile) {
    return (
      <div className="min-h-screen bg-[var(--bg-base)] flex items-center justify-center">
        <p className="text-sm text-[var(--text-secondary)]">Cargando perfil...</p>
      </div>
    );
  }

  const publicUrl = `${window.location.origin}/agente/${profile.username}`;

  const copyToClipboard = () => {
    navigator.clipboard.writeText(publicUrl);
    alert('¡Enlace copiado al portapapeles!');
  };

  const handleLandingToggle = async () => {
    const next = !isLandingActive;
    setIsLandingActive(next);
    setSaving(true);
    try {
      await updateOwnProfile({ landing_active: next });
    } catch {
      setIsLandingActive(!next);
      alert('No se pudo actualizar la landing');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePhone = async (e) => {
    e.preventDefault();
    setSavingPhone(true);
    try {
      await updateOwnProfile({ phone: phone.trim() || null });
    } catch {
      alert('No se pudo guardar el teléfono');
    } finally {
      setSavingPhone(false);
    }
  };

  const handleLogout = async () => {
    setSigningOut(true);
    try {
      await signOut();
      navigate('/login');
    } catch {
      alert('No se pudo cerrar sesión');
      setSigningOut(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg-base)] pb-24 md:pb-12 transition-colors duration-300">
      <div className="bg-[var(--bg-surface)] border-b border-[var(--border-subtle)] pt-12 pb-8 px-6 md:px-12">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-black text-[var(--text-main)] tracking-tighter uppercase italic leading-none mb-2">
            Mi <span className="gfi-gradient-text">Perfil</span>
          </h1>
          <p className="text-[var(--text-secondary)] font-medium uppercase text-xs tracking-[0.2em]">
            Administra tu presencia digital y tus prospectos.
          </p>
        </div>
      </div>

      {profile.agent_code_conflict && (
        <div className="max-w-5xl mx-auto px-6 pt-6">
          <div className="bg-brand-gold/10 border border-brand-gold/40 rounded-2xl p-4 flex items-start gap-3">
            <SafeIcon name="AlertTriangle" className="w-5 h-5 text-brand-gold-dark shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-black text-[var(--text-main)] uppercase tracking-tight">
                Conflicto de Código de Agente pendiente
              </p>
              <p className="text-xs text-[var(--text-secondary)] mt-1">
                Tu código temporal es <strong>{profile.agent_code}</strong>.
                {profile.requested_agent_code && (
                  <> Solicitaste <strong>{profile.requested_agent_code}</strong>.</>
                )}
                {' '}Un administrador lo revisará pronto.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-5xl mx-auto px-6 py-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4 space-y-8">
          <div className="bg-[var(--bg-surface)] p-6 rounded-3xl border border-[var(--border-subtle)] shadow-sm text-center">
            <div className="w-24 h-24 mx-auto rounded-full overflow-hidden border-2 border-[var(--border-subtle)] mb-4 bg-brand-navy flex items-center justify-center text-3xl font-black text-white">
              {profile.avatar_url ? (
                <img src={profile.avatar_url} alt="Perfil" className="w-full h-full object-cover" />
              ) : (
                profile.full_name?.charAt(0) || '?'
              )}
            </div>
            <h2 className="text-xl font-black text-[var(--text-main)] uppercase italic tracking-tight">
              {profile.full_name}
            </h2>
            <p className="text-[10px] font-bold text-[var(--text-secondary)] uppercase tracking-widest mt-1 mb-2">
              {getRoleLabel(profile.role)}
            </p>
            <p className="text-[10px] font-black text-brand-cyan uppercase tracking-widest mb-2">
              Código: {profile.agent_code}
            </p>
            <p className="text-xs text-[var(--text-secondary)] mb-4">{profile.email}</p>
            <form onSubmit={handleSavePhone} className="text-left space-y-2 mb-4">
              <label className="block text-[10px] font-black uppercase tracking-widest text-[var(--text-secondary)]">
                Teléfono de contacto
              </label>
              <div className="flex gap-2">
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="(555) 123-4567"
                  className="flex-1 px-3 py-2 rounded-xl bg-[var(--bg-base)] border border-[var(--border-subtle)] text-sm"
                />
                <button
                  type="submit"
                  disabled={savingPhone}
                  className="px-3 py-2 rounded-xl border border-brand-cyan text-brand-cyan text-[10px] font-black uppercase tracking-widest disabled:opacity-50"
                >
                  {savingPhone ? '…' : 'Guardar'}
                </button>
              </div>
              <p className="text-[10px] text-[var(--text-secondary)]">
                Se usa al reclamar cupos del calendario de sesiones en vivo.
              </p>
            </form>
            <p className="text-[10px] text-[var(--text-secondary)]">
              El código de agente no es editable. Contacta a un administrador si necesitas un cambio.
            </p>
          </div>

          <button
            type="button"
            onClick={handleLogout}
            disabled={signingOut}
            className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl border-2 border-red-500/40 text-red-500 hover:bg-red-500/10 disabled:opacity-50 font-black text-sm uppercase tracking-widest transition-colors"
          >
            <SafeIcon name="LogOut" className="w-5 h-5" />
            {signingOut ? 'Cerrando sesión…' : 'Cerrar sesión'}
          </button>

          <div className="bg-[var(--bg-surface)] p-6 md:p-8 rounded-3xl border border-[var(--border-subtle)] shadow-sm">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h3 className="text-sm font-black text-[var(--text-main)] uppercase tracking-[0.2em] mb-1">
                  Página Personal
                </h3>
                <p className="text-xs text-[var(--text-secondary)]">
                  Tu landing page pública de captación.
                </p>
              </div>
              
              <button 
                onClick={handleLandingToggle}
                disabled={saving}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                  isLandingActive ? 'bg-brand-green' : 'bg-[var(--border-subtle)]'
                }`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  isLandingActive ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>

            {isLandingActive ? (
              <div className="animate-fade-in space-y-4">
                <div className="bg-[var(--bg-base)] border border-[var(--border-subtle)] rounded-xl p-3 flex items-center justify-between gap-2 overflow-hidden">
                  <span className="text-xs text-[var(--text-main)] truncate font-medium opacity-80">
                    {publicUrl}
                  </span>
                  <button 
                    onClick={copyToClipboard}
                    className="p-2 bg-[var(--bg-surface)] rounded-lg hover:bg-[var(--border-subtle)] transition-colors text-[var(--text-main)] flex-shrink-0"
                    title="Copiar Enlace"
                  >
                    <SafeIcon name="Copy" className="w-4 h-4" />
                  </button>
                </div>
                <Link 
                  to={`/agente/${profile.username}`}
                  target="_blank"
                  className="w-full block text-center border border-brand-cyan text-brand-cyan py-3 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-brand-cyan hover:text-white transition-colors"
                >
                  Ver mi página
                </Link>
              </div>
            ) : (
              <div className="bg-[var(--bg-base)] border border-[var(--border-subtle)] rounded-xl p-4 text-center">
                <SafeIcon name="EyeOff" className="w-6 h-6 text-[var(--text-secondary)] mx-auto mb-2 opacity-50" />
                <p className="text-xs text-[var(--text-secondary)]">
                  Activa tu página para empezar a recibir prospectos directamente en tu perfil.
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-8">
          <div className="bg-[var(--bg-surface)] rounded-3xl border border-[var(--border-subtle)] shadow-sm overflow-hidden flex flex-col h-full min-h-[500px]">
            <div className="p-6 md:p-8 border-b border-[var(--border-subtle)] flex items-center justify-between bg-[var(--bg-surface)]">
              <div>
                <h2 className="text-2xl font-black text-[var(--text-main)] uppercase italic tracking-tight flex items-center gap-2">
                  <SafeIcon name="Users" className="w-6 h-6 text-brand-cyan" />
                  Mis Leads
                </h2>
                <p className="text-xs text-[var(--text-secondary)] mt-1">
                  Contactos recibidos desde tu página personal.
                </p>
              </div>
              <div className="w-10 h-10 rounded-full bg-[var(--bg-base)] flex items-center justify-center font-black text-[var(--text-main)] shadow-inner border border-[var(--border-subtle)]">
                {mockLeads.length}
              </div>
            </div>

            <div className="p-6 overflow-y-auto w-full">
              {!isLandingActive && mockLeads.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-center opacity-50">
                  <SafeIcon name="Inbox" className="w-12 h-12 text-[var(--text-secondary)] mb-4" />
                  <p className="text-sm text-[var(--text-secondary)]">No tienes prospectos aún.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {mockLeads.map((lead) => (
                    <div key={lead.id} className="bg-[var(--bg-base)] border border-[var(--border-subtle)] p-5 rounded-2xl hover:border-brand-cyan/50 transition-colors group">
                      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-1">
                            <h3 className="font-black text-[var(--text-main)] text-lg uppercase tracking-tight">
                              {lead.name}
                            </h3>
                            {lead.status === 'new' && (
                              <span className="px-2 py-0.5 bg-brand-green text-white text-[8px] font-black uppercase tracking-widest rounded-full">
                                Nuevo
                              </span>
                            )}
                          </div>
                          <p className="text-[10px] font-bold text-[var(--text-secondary)] uppercase tracking-widest mb-3">
                            Recibido el {lead.date}
                          </p>
                          
                          {lead.message && (
                            <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] p-3 rounded-xl mb-4">
                              <p className="text-sm text-[var(--text-main)]/80 italic">"{lead.message}"</p>
                            </div>
                          )}

                          <div className="flex flex-wrap gap-2">
                            <a href={`mailto:${lead.email}`} className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-lg text-xs font-bold text-[var(--text-main)] hover:bg-[var(--border-subtle)] transition-colors">
                              <SafeIcon name="Mail" className="w-3.5 h-3.5 text-brand-cyan" /> {lead.email}
                            </a>
                            {lead.phone && (
                              <a href={`tel:${lead.phone}`} className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-lg text-xs font-bold text-[var(--text-main)] hover:bg-[var(--border-subtle)] transition-colors">
                                <SafeIcon name="Phone" className="w-3.5 h-3.5 text-brand-green" /> {lead.phone}
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
